import { memo } from "react";
import numeral from "numeral";
import type { Status as TransactionStatus } from "types";
import { Filters, Status } from "consts/transactions";
import { BooleanSwitchValue } from "consts/general";
import { fNumber } from "util/formaters";
import { useQuery } from "hooks/useQuery";
import type { TransactionsResponse } from "features/transactions/types";
import { getTransactions } from "features/transactions/api";
import {
    esTransactionsGroupByStatusQueryToken,
    useStatisticDashboardContext,
    useStatisticDashboardUtils
} from "../StatisticDashboardProvider";
import Cassette from "./Cassette";
import { distinctEmailTransactionsRequestQueryParamsFactory } from "../StatisticDashboardProvider";

type Props = {
    readonly statusLabel: string;
    readonly status: TransactionStatus;
};

const TransactionStatusCassette = ({
    statusLabel,
    status,
    ...restProps
}: Props) => {
    const initialState = {
        isLoading: false
    };

    const distinctEmailsBySucceededTransactionsQuery = useQuery<TransactionsResponse>({
        getRequestQueryParams: requestQueryMapper =>
            distinctEmailTransactionsRequestQueryParamsFactory(Status.Succeeded)(
                requestQueryMapper
                    .contains(Filters.parentId, `${BooleanSwitchValue.Off}`)
            ),
        fetchQuery: getTransactions,
        initialState
    });

    const distinctEmailsByFailedTransactionsQuery = useQuery<TransactionsResponse>({
        getRequestQueryParams: distinctEmailTransactionsRequestQueryParamsFactory(Status.Failed),
        fetchQuery: getTransactions,
        initialState
    });

    const distinctEmailsByRejectedTransactionsQuery = useQuery<TransactionsResponse>({
        getRequestQueryParams: distinctEmailTransactionsRequestQueryParamsFactory(Status.Rejected),
        fetchQuery: getTransactions,
        initialState
    });

    const statisticDashboard = useStatisticDashboardContext();

    const {
        getTransactionAmountByStatus,
        getTransactionAmountPercentageByStatus
    } = useStatisticDashboardUtils();

    const getUniqueEmailCount = (response: TransactionsResponse) => response.data.total;

    return (
        <Cassette
            {...restProps}
            isLoading={statisticDashboard[esTransactionsGroupByStatusQueryToken].isLoading}
            labelSlot={statusLabel}
            amountSlot={fNumber(getTransactionAmountByStatus(status)[0])}
            chipProps={{
                label: numeral(getTransactionAmountPercentageByStatus({
                    status
                }))
                    .format('0.00%'),
            }}
            footerSlot='Unique emails'
            tooltipProps={{
                title: 'Calculate unique emails'
            }}
            lazyQuery={new Map<TransactionStatus, ReturnType<typeof useQuery<TransactionsResponse>>>()
                .set(Status.Succeeded, distinctEmailsBySucceededTransactionsQuery)
                .set(Status.Failed, distinctEmailsByFailedTransactionsQuery)
                .set(Status.Rejected, distinctEmailsByRejectedTransactionsQuery)
                .get(status)!}
            countGetter={getUniqueEmailCount}
        />
    );
};

export default memo(TransactionStatusCassette);
